import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, Users, Banknote } from "lucide-react";
import AIScoreBadge from "../entity-detail/AIScoreBadge";

/**
 * Panel ROI réel d'une audience publicitaire.
 * Affiche les vrais KPI (leads générés / convertis / CA réel) vs prédiction IA.
 */
const eur = (n) =>
  Number(n || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

const STATUS_LABELS = {
  pending: { color: "#64748b", icon: Loader2, label: "En attente" },
  warning: { color: "#f59e0b", icon: AlertTriangle, label: "Vigilance" },
  underperform: { color: "#f43f5e", icon: TrendingDown, label: "Sous-performance" },
  success: { color: "#22c55e", icon: CheckCircle2, label: "Performance ✓" },
};

export default function AudienceROIPanel({ api, audienceId, testId = "audience-roi-panel" }) {
  const [roi, setRoi] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!audienceId) return;
    const load = async () => {
      try {
        const [r, l] = await Promise.all([
          api.get(`/google/audiences/${audienceId}/roi`),
          api.get(`/google/audiences/${audienceId}/leads`),
        ]);
        setRoi(r.data);
        setLeads(l.data || []);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [api, audienceId]);

  if (loading) {
    return (
      <div className="bg-[#0b0811] rounded-xl border border-white/5 p-4" data-testid={`${testId}-loading`}>
        <div className="flex items-center gap-2 text-slate-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Calcul du ROI…
        </div>
      </div>
    );
  }

  if (!roi) return null;

  const fb = STATUS_LABELS[roi.feedback_to_ai?.status] || STATUS_LABELS.pending;
  const FbIcon = fb.icon;
  const deltaPositive = roi.delta_roas >= 0;

  return (
    <div className="bg-[#0b0811] rounded-xl border border-white/5 p-4 space-y-3" data-testid={testId}>
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center">
          <Banknote className="w-4 h-4 text-emerald-400" />
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-bold text-white">ROI de l'audience</h4>
          <p className="text-[10px] text-slate-500">Boucle d'apprentissage IA — feedback réel des campagnes</p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        <KpiCell label="Leads générés" value={roi.leads_generated} icon={Users} accent="#3b82f6" />
        <KpiCell label="Conversions" value={`${roi.conversion_count}`} sub={`${roi.conversion_rate}%`} icon={CheckCircle2} accent="#22c55e" />
        <KpiCell label="CA réel" value={eur(roi.ca_real_eur)} sub={`Budget ${eur(roi.budget_total_eur)}`} icon={Banknote} accent="#f7c935" />
        <div className="bg-[#14101c] border border-white/5 rounded-lg p-2.5 text-center">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">ROAS</div>
          <div className={`text-lg font-bold ${deltaPositive ? "text-emerald-400" : "text-rose-400"}`}>
            x{roi.actual_roas}
          </div>
          <div className="text-[10px] text-slate-500">prévu x{roi.expected_roas}</div>
        </div>
      </div>

      {/* Status by status */}
      {roi.by_status && Object.keys(roi.by_status).length > 0 && (
        <div className="bg-[#14101c] rounded-lg border border-white/5 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Répartition statuts</div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(roi.by_status).map(([status, count]) => (
              <span key={status} className="text-[11px] px-2 py-1 rounded-full bg-white/5 text-slate-300">
                <strong>{count}</strong> {status}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* AI feedback */}
      <div
        className="rounded-lg p-3 flex items-start gap-2"
        style={{ background: fb.color + "11", border: `1px solid ${fb.color}33` }}
        data-testid="ai-feedback"
      >
        <FbIcon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: fb.color }} />
        <div className="text-xs">
          <span className="font-bold uppercase tracking-wider" style={{ color: fb.color }}>{fb.label} —</span>{" "}
          <span className="text-slate-300">{roi.feedback_to_ai?.message}</span>
        </div>
      </div>

      {/* Top leads */}
      {leads.length > 0 && (
        <div className="bg-[#14101c] rounded-lg border border-white/5 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Leads attachés ({leads.length})</div>
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {leads.slice(0, 8).map((l) => {
              const ai = l.ai_evaluation || {};
              const fn = `${l.first_name || ""} ${l.last_name || ""}`.trim() || l.email;
              return (
                <Link
                  key={l.prospect_id}
                  to={`/admin/prospect/${l.prospect_id}`}
                  className="flex items-center gap-2 text-xs text-slate-300 hover:text-white px-2 py-1 rounded hover:bg-white/5"
                  data-testid={`roi-lead-${l.prospect_id}`}
                >
                  <span className="flex-1 truncate">{fn}</span>
                  <span className="text-[10px] text-slate-500">{l.status}</span>
                  {ai.score !== undefined && <AIScoreBadge tier={ai.tier} score={ai.score} size="sm" />}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const KpiCell = ({ label, value, sub, icon: Icon, accent }) => (
  <div className="bg-[#14101c] border border-white/5 rounded-lg p-2.5">
    <div className="flex items-center gap-1.5">
      <Icon className="w-3 h-3" style={{ color: accent }} />
      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</span>
    </div>
    <div className="text-lg font-bold text-white mt-1">{value}</div>
    {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
  </div>
);
